/**
 * HipStirs overhaul — shared behaviour for every hs-* overhaul section.
 * Ported from the owner-approved comp (docs/comps/comp-template.html, v8).
 * Each feature keys off a data attribute so sections can be added, removed
 * or re-rendered in the theme editor independently. Cart adds reuse the
 * theme's own event contract (same as hs-build-a-pack.js) so the cart
 * drawer opens and the header count refreshes without bespoke plumbing.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
export const money = (cents) => {
  const fmt = (window.Theme && Theme.moneyFormat) || '${{amount}}';
  return fmt.replace(/\{\{\s*amount[^}]*\}\}/, (cents / 100).toFixed(2));
};

/* ---------- cart ---------- */
function sectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
  });
  return ids.join(',');
}

export function addToCart(target, items, source = 'hs-overhaul') {
  const totalQuantity = items.reduce((s, i) => s + i.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
      promise: deferred.promise,
    })
  );
  return fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sectionIds() }),
  })
    .then((r) => r.json())
    .then((response) => {
      if (response.status) {
        const message = response.message || 'Add to cart failed';
        target.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: { errors: response.errors } }));
        deferred.reject(new Error(message));
        throw new Error(message);
      }
      return fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source, sourceId: source, itemCount: totalQuantity, sections: response.sections, didError: false },
          });
          return cart;
        });
    })
    .catch((error) => {
      target.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      throw error;
    });
}

function busy(btn, on) {
  if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
  btn.classList.toggle('is-busy', on);
}

/* [data-hs-add] buttons (delegated, so buttons re-rendered inside the cart drawer keep working):
   variant id in data-variant-id, optional quantity source in data-qty-from */
function initAddButtons() {
  if (document.documentElement.dataset.hsAddBound) return;
  document.documentElement.dataset.hsAddBound = '1';
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('[data-hs-add]') : null;
    if (!btn || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    const id = Number(btn.dataset.variantId);
    if (!id) return;
    const qtyEl = btn.dataset.qtyFrom ? document.querySelector(btn.dataset.qtyFrom) : null;
    const quantity = Math.max(1, parseInt(qtyEl ? qtyEl.value || qtyEl.textContent : '1', 10) || 1);
    busy(btn, true);
    addToCart(btn, [{ id, quantity }])
      .then(() => { btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 1400); })
      .catch(() => {})
      .finally(() => busy(btn, false));
  });
}

/* Size toggles inside a tile: each button carries data-variant + data-price; updates the sibling price <b> and the tile's add button */
function initSizes(scope) {
  scope.querySelectorAll('.sizes button').forEach((b) => {
    if (b.dataset.hsBound) return;
    b.dataset.hsBound = '1';
    b.addEventListener('click', (e) => {
      e.preventDefault();
      const g = b.parentNode;
      g.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', 'false'));
      b.setAttribute('aria-pressed', 'true');
      const host = b.closest('[data-hs-tile]') || b.closest('.meta') || b.closest('.row');
      if (!host) return;
      const price = host.querySelector('[data-price-out]') || (b.closest('.meta') || b.closest('.row') || host).querySelector('b');
      if (price && b.dataset.price) price.textContent = b.dataset.price;
      const add = host.querySelector('[data-hs-add]');
      if (add && b.dataset.variant) add.dataset.variantId = b.dataset.variant;
      host.dispatchEvent(new CustomEvent('hs:size', { bubbles: true, detail: { variant: b.dataset.variant, price: b.dataset.price, size: b.textContent.trim() } }));
    });
  });
}

/* ---------- generic dots + arrows ---------- */
function carousel(nav, count, onGo) {
  const dots = nav.querySelector('.dots');
  const arrows = nav.querySelectorAll('.arrow');
  let idx = 0;
  dots.innerHTML = '';
  for (let i = 0; i < count; i++) {
    const b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('aria-label', 'Go to ' + (i + 1));
    b.addEventListener('click', () => go(i));
    dots.appendChild(b);
  }
  arrows.forEach((a) => { a.onclick = () => go(idx + parseInt(a.dataset.dir, 10)); });
  function go(n) {
    idx = Math.max(0, Math.min(count - 1, n));
    dots.querySelectorAll('button').forEach((d, i) => d.setAttribute('aria-current', i === idx ? 'true' : 'false'));
    if (arrows[0]) arrows[0].disabled = idx === 0;
    if (arrows[1]) arrows[1].disabled = idx === count - 1;
    onGo(idx);
  }
  go(0);
  return { go, get idx() { return idx; }, count };
}

/* ---------- syrup rail ---------- */
function initRail(root) {
  const rail = root.querySelector('.rail');
  const wrap = root.querySelector('.rail-wrap');
  const nav = root.querySelector('.carousel-nav');
  if (!rail || !nav) return;
  const tiles = rail.querySelectorAll('.tile');
  if (!tiles.length) return;
  const perPage = () => {
    const t = tiles[0].getBoundingClientRect().width + 18;
    return Math.max(1, Math.floor(rail.clientWidth / t));
  };
  let ctl;
  const layout = () => {
    const pp = perPage();
    const pages = Math.ceil(tiles.length / pp);
    ctl = carousel(nav, pages, (i) => {
      const t = tiles[0].getBoundingClientRect().width + 18;
      rail.scrollTo({ left: i * pp * t, behavior: reduce ? 'auto' : 'smooth' });
    });
  };
  layout();
  let rt;
  window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(layout, 150); });
  const edges = () => {
    const max = rail.scrollWidth - rail.clientWidth;
    const atEnd = rail.scrollLeft >= max - 8;
    wrap.classList.toggle('end', atEnd);
    wrap.classList.toggle('mid', rail.scrollLeft > 8 && !atEnd);
  };
  rail.addEventListener('scroll', edges, { passive: true });
  edges();
}

/* ---------- recipe orbits (track of full-width pages) ---------- */
function initTrack(root) {
  const track = root.querySelector('.rec-track');
  const nav = root.querySelector('.carousel-nav');
  if (!track || !nav) return;
  const pages = track.children.length;
  if (pages < 2) { nav.hidden = true; return; }
  carousel(nav, pages, (i) => { track.style.transform = 'translateX(-' + i * 100 + '%)'; });
}

/* ---------- reviews rotator ---------- */
function initReviews(root) {
  const photos = root.querySelectorAll('.rphoto img');
  const slides = root.querySelectorAll('.rslide');
  const nav = root.querySelector('.carousel-nav');
  if (!slides.length || !nav) return;
  if (slides.length < 2) { nav.hidden = true; return; }
  carousel(nav, slides.length, (i) => {
    photos.forEach((p, k) => p.classList.toggle('on', k === i));
    slides.forEach((s, k) => s.classList.toggle('on', k === i));
  });
}

/* ---------- pack ladder rise ---------- */
function initLadder(ladder) {
  /* --f: each mini bottle's position from the tier's centre, -1 (left edge) … 0 … 1 (right edge); drives the hover fan-out */
  ladder.querySelectorAll('.minis').forEach((m) => {
    const imgs = [...m.querySelectorAll('img')];
    const mid = (imgs.length - 1) / 2;
    imgs.forEach((img, i) => img.style.setProperty('--f', mid ? ((i - mid) / mid).toFixed(3) : '0'));
  });
  if (reduce || !('IntersectionObserver' in window)) { ladder.classList.add('go', 'done'); return; }
  const io = new IntersectionObserver((es) => {
    es.forEach((e) => {
      if (e.isIntersecting) {
        ladder.classList.add('go');
        setTimeout(() => ladder.classList.add('done'), 1200);
        io.disconnect();
      }
    });
  }, { threshold: 0.3 });
  io.observe(ladder);
}

/* ---------- scroll reveal (visible at rest) ---------- */
function initReveal(scope) {
  if (reduce || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.remove('pre'); io.unobserve(e.target); } });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
  scope.querySelectorAll('.rv').forEach((el) => {
    if (el.dataset.hsRv) return;
    el.dataset.hsRv = '1';
    if (el.getBoundingClientRect().top > window.innerHeight) { el.classList.add('pre'); io.observe(el); }
  });
}

/* ---------- PDP ---------- */
function initPdp(root) {
  /* Gallery: the main image is a scroll-snap track (swipe on touch); thumbs scroll it and follow it */
  const track = root.querySelector('[data-pdp-track]');
  const thumbs = [...root.querySelectorAll('.thumbs button')];
  const setThumb = (i) => thumbs.forEach((b, j) => {
    b.setAttribute('aria-current', j === i ? 'true' : 'false');
    if (j === i) b.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  });
  thumbs.forEach((b, i) => b.addEventListener('click', () => {
    if (track) track.scrollTo({ left: i * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    setThumb(i);
  }));
  if (track) {
    let raf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setThumb(Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
    }, { passive: true });
    track.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      track.scrollBy({ left: (e.key === 'ArrowRight' ? 1 : -1) * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    });
  }
  const form = root.querySelector('form[data-pdp-form]');
  const idInput = form?.querySelector('input[name="id"]');
  const priceOut = root.querySelector('[data-pdp-price]');
  const noteOut = root.querySelector('[data-size-note]');
  const setVariant = (b) => {
    if (idInput) idInput.value = b.dataset.variant;
    if (priceOut) priceOut.innerHTML = b.dataset.price + (b.dataset.note ? ' <small>' + b.dataset.note + '</small>' : '');
    if (noteOut) noteOut.textContent = b.dataset.size || '';
    const url = new URL(location.href);
    url.searchParams.set('variant', b.dataset.variant);
    history.replaceState({}, '', url);
    form?.dispatchEvent(new CustomEvent('hs:variant', { bubbles: true, detail: { id: b.dataset.variant } }));
  };
  root.querySelectorAll('[data-pdp-size] button').forEach((b) => {
    b.addEventListener('click', () => {
      root.querySelectorAll('[data-pdp-size] button').forEach((x) => x.setAttribute('aria-pressed', 'false'));
      b.setAttribute('aria-pressed', 'true');
      setVariant(b);
    });
  });
  const qty = root.querySelector('[data-qty-out]');
  const qtyInput = form?.querySelector('input[name="quantity"]');
  root.querySelectorAll('.qty button').forEach((b) => {
    b.addEventListener('click', () => {
      const v = Math.max(1, parseInt(qty.value || qty.textContent, 10) + parseInt(b.dataset.d, 10));
      qty.value = v; qty.textContent = v;
      if (qtyInput) qtyInput.value = v;
    });
  });
  if (form) {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const fd = new FormData(form);
      const id = Number(fd.get('id'));
      const quantity = Math.max(1, parseInt(fd.get('quantity') || '1', 10));
      const item = { id, quantity };
      const plan = fd.get('selling_plan');
      if (plan) item.selling_plan = Number(plan);
      busy(btn, true);
      addToCart(form, [item], 'hs-product').catch(() => {}).finally(() => busy(btn, false));
    });
  }
}

/* ---------- PDP sticky add to cart: shows once the buy row scrolls above the viewport ---------- */
function initPdpSticky(root) {
  const bar = root.querySelector('[data-pdp-sticky]');
  const row = root.querySelector('.qtyrow');
  const form = root.querySelector('form[data-pdp-form]');
  if (!bar || !row || !form || !('IntersectionObserver' in window)) return;
  const priceOut = root.querySelector('[data-pdp-price]');
  const stickyPrice = bar.querySelector('[data-pdp-sticky-price]');
  const syncPrice = () => { if (priceOut && stickyPrice) stickyPrice.textContent = priceOut.childNodes[0]?.textContent.trim() || priceOut.textContent.trim(); };
  new IntersectionObserver(([e]) => {
    const show = !e.isIntersecting && e.boundingClientRect.top < 0;
    syncPrice();
    bar.hidden = !show;
    document.documentElement.classList.toggle('hs-sticky-atc', show);
  }).observe(row);
  bar.querySelector('[data-pdp-sticky-add]')?.addEventListener('click', () => form.requestSubmit());
  form.addEventListener('hs:variant', () => setTimeout(syncPrice, 0));
}

/* ---------- horizontal scrollers: edge masks that track scroll position ---------- */
function initScrollMask(el) {
  if (el.dataset.hsMaskBound) return;
  el.dataset.hsMaskBound = '1';
  const update = () => {
    const max = el.scrollWidth - el.clientWidth;
    el.style.setProperty('--ml', Math.min(40, Math.max(0, el.scrollLeft)) + 'px');
    el.style.setProperty('--mr', Math.min(40, Math.max(0, max - el.scrollLeft)) + 'px');
  };
  el.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
}

/* ---------- collection filters ----------
   The filter buttons (desktop) and the Show dropdown (phones) link to collections.
   ?show=16 (and syrup/pack/of) still filters the current page in place; with 16 the
   two-size cards switch to their 16 oz variant. */
function initCollectionFilters(root) {
  const nav = root.querySelector('[data-cf-nav]');
  const seg = root.querySelector('[data-cf-seg]');
  const sort = root.querySelector('[data-cf-sort]');
  const grid = root.querySelector('[data-cgrid]');
  const empty = root.querySelector('[data-cempty]');
  const valid = ['syrup', 'pack', 'of', '16'];
  const shown = new URL(location.href).searchParams.get('show');
  const t = valid.indexOf(shown) >= 0 ? shown : 'all';
  if (grid && t !== 'all') {
    if (t === '16') initSizes(grid); // bind the size toggles before clicking them
    let n = 0;
    grid.querySelectorAll('.tile').forEach((el) => {
      const ok = el.dataset.type === t || (t === '16' && el.dataset.size16 === '1') || (t === 'of' && el.dataset.of === '1');
      el.hidden = !ok;
      if (!ok) return;
      n++;
      if (t === '16') {
        const b16 = [...el.querySelectorAll('.sizes button')].find((b) => /16/.test(b.textContent));
        if (b16 && b16.getAttribute('aria-pressed') !== 'true') b16.click();
      }
    });
    const promo = grid.querySelector('.promo');
    if (promo) promo.hidden = t === 'of';
    if (empty) empty.hidden = n > 0;
    const count = document.querySelector('[data-hs-count]');
    if (count) count.textContent = `${n} ${n === 1 ? 'product' : 'products'}`;
    // The page's own button no longer matches; mark the filter that is showing.
    seg?.querySelectorAll('a').forEach((a) => (a.dataset.v === t ? a.setAttribute('aria-current', 'page') : a.removeAttribute('aria-current')));
    if (nav) [...nav.options].forEach((o) => { o.selected = o.dataset.v === t; });
  }
  nav?.addEventListener('change', () => { location.href = nav.value; });
  sort?.addEventListener('change', () => {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', sort.value);
    location.href = url.toString();
  });
  // A promo that ends a short collection fills the rest of the last row (full row if under 2 cells are left).
  // In a filtered view the promo also ends the visible grid, so it gets the same treatment.
  const promo = grid?.querySelector(t === 'all' ? '.promo[data-promo-end]' : '.promo:not([hidden])');
  if (promo) {
    const fit = () => {
      const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').length;
      const tiles = [...grid.querySelectorAll('.tile')].filter((el) => !el.hidden).length;
      const left = cols - (tiles % cols);
      promo.style.gridColumn = cols > 2 && left >= 2 && left < cols ? `span ${left}` : '1 / -1';
    };
    fit();
    addEventListener('resize', fit);
  }
}

/* ---------- homepage hero carousel (round 12) ---------- */
function initHero(hero) {
  if (hero.dataset.hsHeroBound) return;
  hero.dataset.hsHeroBound = '1';
  const slides = [...hero.querySelectorAll('[data-hero-slide]')];
  if (slides.length < 2) return;
  const dots = [...hero.querySelectorAll('.dots button')];
  const FADE = 750; // matches the .hero-slide opacity transition
  let idx = 0, timer = null, leaveTimer = null;
  const holds = new Set(); // reasons autoplay is held: 'controls', 'keyboard', 'editor'
  // Layered crossfade: the outgoing slide stays opaque underneath while the incoming one fades in on top,
  // so the page ground never shows through mid-transition. Rapid clicks settle the previous transition first.
  const show = (i, user) => {
    i = (i + slides.length) % slides.length;
    if (i === idx) { if (user) schedule(); return; }
    const prev = slides[idx], next = slides[i];
    clearTimeout(leaveTimer);
    slides.forEach((s) => { if (s !== prev) s.classList.remove('is-leaving'); });
    prev.classList.remove('is-active', 'is-entering');
    prev.classList.add('is-leaving');
    next.classList.remove('is-leaving', 'is-entering');
    void next.offsetWidth; // restart the entrance animations
    next.classList.add('is-active');
    if (!reduce) next.classList.add('is-entering');
    slides.forEach((s) => {
      const on = s === next;
      s.toggleAttribute('inert', !on);
      s.setAttribute('aria-hidden', String(!on));
    });
    leaveTimer = setTimeout(() => {
      prev.classList.remove('is-leaving');
      next.classList.remove('is-entering');
    }, reduce ? 0 : FADE + 500);
    dots.forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    idx = i;
    schedule();
  };
  const autoplay = hero.dataset.autoplay === 'true' && !reduce;
  const ms = (Number(hero.dataset.interval) || 7) * 1000;
  // One timeout per slide: every change (auto or user) gets a full interval, and releasing a hold restarts it.
  const schedule = () => {
    clearTimeout(timer);
    if (autoplay && !holds.size) timer = setTimeout(() => { if (document.hidden) schedule(); else show(idx + 1); }, ms);
  };
  const hold = (why, on) => { if (on) holds.add(why); else holds.delete(why); schedule(); };
  hero.querySelectorAll('.arrow').forEach((a) => a.addEventListener('click', () => show(idx + Number(a.dataset.dir), true)));
  dots.forEach((d) => d.addEventListener('click', () => show(Number(d.dataset.go), true)));
  // Hold only while a mouse is over the controls or the buttons (the hero fills the screen, so a whole-hero hover
  // froze it), or while keyboard focus is inside. Mouse clicks focus buttons but don't hold.
  hero.querySelectorAll('.hero-nav .carousel-nav, .ctas').forEach((el) => {
    el.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hold('controls', true); });
    el.addEventListener('pointerleave', () => hold('controls', false));
  });
  hero.addEventListener('focusin', (e) => { if (e.target.matches(':focus-visible')) hold('keyboard', true); });
  hero.addEventListener('focusout', (e) => { if (!hero.contains(e.relatedTarget)) hold('keyboard', false); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  // swipe
  let x0 = null, y0 = 0;
  hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
  hero.addEventListener('touchend', (e) => {
    if (x0 === null) return;
    const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) show(idx + (dx < 0 ? 1 : -1), true);
    x0 = null;
  });
  // Warm the later slides' images once the page has loaded, so bottles don't pop in mid-fade
  const warm = () => hero.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  if (document.readyState === 'complete') warm(); else window.addEventListener('load', warm, { once: true });
  // theme editor: selecting a slide block shows it and holds autoplay
  document.addEventListener('shopify:block:select', (e) => {
    const k = slides.findIndex((s) => e.target.contains(s));
    if (k > -1) { holds.add('editor'); show(k); }
  });
  document.addEventListener('shopify:block:deselect', () => hold('editor', false));
  schedule();
}

/* ---------- recipes hub showcase hero ---------- */
function initHubHero(hero) {
  const slides = hero.querySelectorAll('.slide');
  const rings = hero.querySelectorAll('.art .ring');
  const nav = hero.querySelector('.carousel-nav');
  const prog = hero.querySelector('.prog i');
  if (slides.length < 2) { if (nav) nav.hidden = true; if (prog) prog.parentNode.hidden = true; return; }
  let idx = 0;
  const show = (i) => {
    idx = i;
    slides.forEach((s, k) => s.classList.toggle('on', k === i));
    rings.forEach((r, k) => r.classList.toggle('on', k === i));
    nav.querySelectorAll('.dots button').forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    if (prog) { prog.style.animation = 'none'; void prog.offsetWidth; prog.style.animation = ''; }
  };
  const dots = nav.querySelector('.dots');
  dots.innerHTML = '';
  slides.forEach((_, i) => {
    const b = document.createElement('button');
    b.type = 'button'; b.setAttribute('aria-label', 'Showcase ' + (i + 1));
    b.addEventListener('click', () => show(i));
    dots.appendChild(b);
  });
  nav.querySelectorAll('.arrow').forEach((a) => a.addEventListener('click', () => show((idx + parseInt(a.dataset.dir, 10) + slides.length) % slides.length)));
  show(0);
  if (!reduce) setInterval(() => { if (!hero.matches(':hover')) show((idx + 1) % slides.length); }, 5000);
}

/* ---------- general page side nav ---------- */
function initPageNav(root) {
  const links = root.querySelectorAll('.gp-nav a');
  const heads = root.querySelectorAll('.rich h2[id]');
  if (!links.length || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((es) => {
    es.forEach((e) => {
      if (e.isIntersecting) links.forEach((a) => a.classList.toggle('on', a.getAttribute('href') === '#' + e.target.id));
    });
  }, { rootMargin: '-20% 0px -70% 0px' });
  heads.forEach((h) => io.observe(h));
}

/* ---------- header ---------- */
function initHeader(root) {
  const btn = root.querySelector('[data-hs-menu-toggle]');
  const drawer = root.querySelector('[data-hs-menu]');
  if (!btn || !drawer) return;
  const header = root.querySelector('.nav');
  btn.addEventListener('click', () => {
    /* the drawer lives outside <header> (its backdrop-filter would clip a fixed child); pin it under the sticky nav */
    if (header) drawer.style.setProperty('--hs-menu-top', Math.max(0, header.getBoundingClientRect().bottom) + 'px');
    const open = drawer.toggleAttribute('data-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.documentElement.classList.toggle('hs-menu-open', open);
  });
  drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => {
    drawer.removeAttribute('data-open'); btn.setAttribute('aria-expanded', 'false'); document.documentElement.classList.remove('hs-menu-open');
  }));
}

/* ---------- spinning orbs: ease the ring's speed up/down from wherever it is ---------- */
function initOrb(orb) {
  if (reduce || orb.dataset.hsBound) return;
  orb.dataset.hsBound = '1';
  const ring = orb.querySelector('svg.ring');
  if (!ring || !ring.getAnimations) return;
  let raf = 0;
  const ease = (target) => {
    cancelAnimationFrame(raf);
    const anim = ring.getAnimations()[0];
    if (!anim) return;
    const from = anim.playbackRate, start = performance.now(), dur = 700;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur), k = 1 - Math.pow(1 - t, 3);
      anim.updatePlaybackRate(from + (target - from) * k);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  };
  orb.addEventListener('pointerenter', () => ease(3));
  orb.addEventListener('pointerleave', () => ease(1));
  orb.addEventListener('focus', () => ease(3));
  orb.addEventListener('blur', () => ease(1));
}

/* ---------- free-shipping tracker (drawer + header bar) ---------- */
export function refreshShip(cart) {
  const apply = (c) => {
    document.querySelectorAll('[data-hs-ship]').forEach((el) => {
      const threshold = Number(el.dataset.threshold) || 5000;
      const total = c.total_price;
      const remaining = threshold - total;
      const msg = el.querySelector('[data-hs-ship-msg]');
      if (msg) msg.innerHTML = remaining > 0 ? `You're <b>${money(remaining)}</b> away from free shipping` : '<b>Free shipping unlocked.</b> Nice pour.';
      const fill = el.querySelector('[data-hs-ship-fill]');
      if (fill) fill.style.width = Math.min(100, Math.floor((total * 100) / threshold)) + '%';
      el.querySelector('[data-hs-ship-track]')?.setAttribute('aria-valuenow', String(Math.min(total, threshold)));
      el.classList.toggle('is-unlocked', remaining <= 0);
    });
    const has = c.item_count > 0;
    document.querySelectorAll('[data-hs-shipbar]').forEach((b) => { b.hidden = !has; });
    document.querySelectorAll('[data-hs-announce]').forEach((a) => { a.hidden = has; });
  };
  if (cart) return apply(cart);
  return fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).then(apply).catch(() => {});
}
function initShip() {
  if (document.documentElement.dataset.hsShipBound) return;
  document.documentElement.dataset.hsShipBound = '1';
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    const done = () => refreshShip();
    if (e.promise) e.promise.then(done, done); else setTimeout(done, 600);
  });
}

/* ---------- cart drawer: Subscribe & save (per syrup + whole cart) with delivery frequency ---------- */
async function changeLine(key, qty, plan) {
  const res = await fetch(`${Theme.routes.cart_change_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity: qty, selling_plan: plan ? Number(plan) : null }),
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}
function initCartSubscribe() {
  if (document.documentElement.dataset.hsSubBound) return;
  document.documentElement.dataset.hsSubBound = '1';
  document.addEventListener('change', async (e) => {
    const el = e.target;
    if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) return;
    const kind = el.matches('[data-hs-line-sub]') ? 'line' : el.matches('[data-hs-line-freq]') ? 'lineFreq'
      : el.matches('[data-hs-all-sub]') ? 'all' : el.matches('[data-hs-all-freq]') ? 'allFreq' : null;
    if (!kind) return;
    const drawer = el.closest('cart-items-component');
    const busyEl = el.closest('.hs-linesub-row, .hs-cartsub-all') || el.closest('label');
    const prevChecked = el instanceof HTMLInputElement ? !el.checked : null;
    el.disabled = true;
    busyEl?.classList.add('is-busy');
    let cart = null, failed = false;
    try {
      if (kind === 'line') {
        cart = await changeLine(el.dataset.key, Number(el.dataset.qty), el.checked ? el.dataset.plan : null);
      } else if (kind === 'lineFreq') {
        cart = await changeLine(el.dataset.key, Number(el.dataset.qty), el.value);
      } else {
        // Whole cart: the bulk frequency is an index into each product's own plan list (plan ids differ per group)
        const idx = Number(drawer?.querySelector('[data-hs-all-freq]')?.value || 0);
        const turnOn = kind === 'allFreq' || el.checked;
        for (const li of drawer?.querySelectorAll('[data-hs-all-lines] li') || []) {
          const plans = li.dataset.plans.split(',');
          const target = turnOn ? plans[Math.min(idx, plans.length - 1)] : null;
          if (kind === 'all' && turnOn && li.dataset.cur) continue; // already subscribed: keep its frequency
          if ((li.dataset.cur || null) === target) continue;
          try { cart = await changeLine(li.dataset.key, Number(li.dataset.qty), target); } catch (_) { failed = true; }
        }
      }
    } catch (_) { failed = true; }
    if (failed && prevChecked !== null && !cart) el.checked = prevChecked;
    const sectionId = drawer?.dataset.sectionId || 'hs-cart-drawer';
    try { await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' }); } catch (_) {}
    el.disabled = false;
    busyEl?.classList.remove('is-busy');
    refreshShip(cart || undefined);
  });
}

/* ---------- boot ---------- */
function boot(scope) {
  scope.querySelectorAll('[data-hs-rail]').forEach(initRail);
  scope.querySelectorAll('[data-hs-track]').forEach(initTrack);
  scope.querySelectorAll('[data-hs-reviews]').forEach(initReviews);
  scope.querySelectorAll('[data-hs-ladder]').forEach(initLadder);
  scope.querySelectorAll('[data-hs-pdp]').forEach(initPdp);
  scope.querySelectorAll('[data-hs-pdp]').forEach(initPdpSticky);
  scope.querySelectorAll('[data-hs-scrollmask]').forEach(initScrollMask);
  scope.querySelectorAll('[data-hs-cfilter]').forEach(initCollectionFilters);
  scope.querySelectorAll('[data-hs-hub-hero]').forEach(initHubHero);
  scope.querySelectorAll('[data-hs-hero]').forEach(initHero);
  scope.querySelectorAll('[data-hs-page]').forEach(initPageNav);
  scope.querySelectorAll('[data-hs-header]').forEach(initHeader);
  scope.querySelectorAll('.orb').forEach(initOrb);
  initShip();
  initCartSubscribe();
  initSizes(scope);
  initAddButtons();
  initReveal(scope);
}
boot(document);
document.addEventListener('shopify:section:load', (e) => boot(e.target));
window.HsOverhaul = { addToCart, boot, money };
